<template>
  <div class="edit-deal-child-deals">
    <div class="form-section group mt-0">
      <span class="icon"><icon-colored-deal /></span>
      {{ capitalize($t('names.deal', { n: 2 })) }}
      <div class="actions">
        <deal-discovery
          :deal="deal"
          :selected-currencies="selectedCurrencies"
          :all-currencies="availableCurrencies"
          @statistics-changed="onDealDiscoveryChanged" />
      </div>
    </div>
    <div
      v-if="deal.packages?.length > 0"
      class="row">
      <div class="col-12">
        <div class="pane-content selected-deals mb-0 pt-1 pb-0">
          <FormSeparator margin-size="sm">{{ $t('names.selectedDeal', { n: 2 }) }}</FormSeparator>
          <div class="mb-3 d-flex flex-wrap">
            <div
              v-for="p in deal.packages"
              :key="p.id"
              v-tooltip="p.name"
              class="badge badge-info-hollow mb-3 mr-3 align-content-center">
              <div class="flex-h">
                <button
                  class="child-deal btn blank clear-btn btn-sm"
                  @click="removeChildDeal(p)">
                  <span class="icon-xxxs">
                    <icon-close />
                  </span>
                </button>
                <span
                  class="mr-2"
                  style="font-weight: normal"
                  >{{ p.dealID }} –
                </span>
                <MediaOwnerComponent :media-owner="suppliers.find((x) => x.id === p.organizationID)" />
              </div>
            </div>
          </div>
          <FormSeparator margin-size="none">{{ $t('names.availableDeal', { n: 2 }) }}</FormSeparator>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <CuratorSupplierDealsTable
          ref="tableRef"
          :active-deal="activeDeal"
          :allow-selection="true"
          :currency="currency"
          :fixed-filters="fixedFilters"
          :suppliers="suppliers"
          :discovered-deals="discoveredDeals"
          :deal-locked-scope-type="dealLockedScopeType"
          @requested-deal-details="onRequestedDealDetails"
          @selection-changed="onSelectionChanged"
          @filters-changed="onFiltersChanged"
          @default-filters-loaded="onDefaultFiltersLoaded" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, Ref, ref, watch } from 'vue';

  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import DealDiscovery from '@/common/components/deals/DealDiscovery.vue';
  import { DealScope, onDealPreviewChanged } from '@/common/components/deals/DealsHelper';
  import CuratorSupplierDealsTable from '@/common/components/deals/form/CuratorSupplierDealsTable.vue';
  import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
  import { IconColoredDeal } from '@/common/icons/colored';
  import { IconClose } from '@/common/icons/monochrome';
  import { FilterCollection, Package, QueryParams } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import DealDiscoveryStatistics from '@/common/models/package/DealDiscovery';
  import Status from '@/common/models/package/PackageStatus.enum';
  import Filter from '@/common/models/query-params/Filter';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import { toServerDateTimeNoTimezone, useInjected } from '@/globals';
  import MediaOwnerComponent from '@/org-app/components/mediaOwners/MediaOwner.vue';
  import { delay } from '@/utils/DelayHelper';
  import { mapActions, mapOrgAppActions } from '@/utils/StateMapping';

  interface IEditDealSustainabilityProps {
    dealLockedScopeType?: DealScope;
    activeDeal?: Package;
    suppliers: MediaOwner[];
  }

  const props = withDefaults(defineProps<IEditDealSustainabilityProps>(), {
    activeDeal: null,
    dealLockedScopeType: null,
  });

  const deal: Ref<Package> = useInjected<Package>('deal');

  const emit = defineEmits(['selectionChanged', 'filtersChanged', 'dealPreviewChanged']);

  const tableRef = ref(null);
  const discoveredDeals = ref<number[]>([]);
  const selectedCurrencies = ref<string[]>([]);

  const { updatePackageMediaOwnerIDs, updatePackagePackageIDs, updatePackagePackages } = mapActions();
  const { getCuratorPackages } = mapOrgAppActions();

  const fixedFilters = computed(() => [
    new Filter(
      'purchase_type',
      [PurchaseTypesEnum.NON_GUARANTEED, PurchaseTypesEnum.PREFERRED],
      FilterComparisonOperator.IN,
    ),
    new Filter(
      'flight_to',
      [toServerDateTimeNoTimezone(deal.value.startDate)],
      FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
    ),
    new Filter('state', [Status.Published]),
  ]);

  const lockedCurrency = computed(() => {
    return deal.value.packages?.length > 0 || discoveredDeals.value.length > 0;
  });

  const currency = computed(() => {
    if (deal.value.packages?.length > 0) {
      return props.suppliers.find((x) => x.id === deal.value.packages[0].organizationID).currency;
    } else if (selectedCurrencies.value.length === 1 && lockedCurrency.value) {
      return selectedCurrencies.value[0];
    } else {
      return null;
    }
  });

  const availableCurrencies = computed(() => {
    return [...new Set(props.suppliers.map((x) => x.currency))];
  });

  async function updatePackages(newPackages: Package[]) {
    updatePackagePackages({ package: deal.value, value: newPackages });

    await delay(100);
    if (newPackages.length === 0 || deal.value.packageIDs == null || deal.value.packageIDs?.length === 0) {
      tableRef.value?.reloadPackages();
    }

    const value = newPackages.map((x) => x.id);
    updatePackagePackageIDs({ package: deal.value, value });
    updatePackageMediaOwnerIDs({
      package: deal.value,
      value: newPackages.length > 0 ? newPackages.map((x) => x.organizationID) : props.suppliers.map((x) => x.id),
    });

    emit('selectionChanged');
  }

  function onFiltersChanged(filters) {
    selectedCurrencies.value = [...(filters['currency']?.included ?? [])];
    emit('filtersChanged', filters);
  }

  function onSelectionChanged(updatedPackages: Package[]) {
    updatePackages(updatedPackages);

    setTimeout(() => {
      if (updatedPackages.length === 0 || deal.value.packageIDs == null || deal.value.packageIDs?.length === 0) {
        tableRef.value?.reloadPackages();
      }

      const value = updatedPackages.map((x) => x.id);
      updatePackagePackageIDs({ package: deal.value, value });
      updatePackageMediaOwnerIDs({
        package: deal.value,
        value:
          updatedPackages.length > 0 ? updatedPackages.map((x) => x.organizationID) : props.suppliers.map((x) => x.id),
      });

      emit('selectionChanged');
    }, 100);
  }

  function removeChildDeal(removedDeal: Package) {
    const newPackages = deal.value.packages?.filter((x) => x.id !== removedDeal.id);
    updatePackages(newPackages || []);
    tableRef.value.table.handleItemSelection(removedDeal, false);
  }

  function onRequestedDealDetails(requestedDeal: Package) {
    const mutation = (change: Package) => emit('dealPreviewChanged', change);
    onDealPreviewChanged(requestedDeal, props.activeDeal, mutation);
  }

  function onDefaultFiltersLoaded(defaultFilters) {
    const currencyFilter = defaultFilters.find((filter) => filter.property === 'currency');
    if (!currencyFilter) return;
    selectedCurrencies.value = currencyFilter.instance.valuesInternal;
  }

  async function onDealDiscoveryChanged(dealDiscoveryResponse: {
    dealDiscoveryStatistics: DealDiscoveryStatistics;
    currencyUsed: string;
  }) {
    const { dealDiscoveryStatistics, currencyUsed } = dealDiscoveryResponse || {};
    discoveredDeals.value = dealDiscoveryStatistics
      ? dealDiscoveryStatistics.dealsMatchingTargeting.map((p) => p.package_id)
      : [];
    if (discoveredDeals.value.length > 0) {
      // Retrieve the packages from the deal discovery endpoint and update the packages in the deal.
      const packages = await getTargetedPackages(discoveredDeals.value);
      updatePackagePackageIDs({ package: deal.value, value: discoveredDeals.value });
      updatePackageMediaOwnerIDs({
        package: deal.value,
        value: packages.map((x: Package) => x.organizationID),
      });
      await updatePackages(packages);
    }
    if (selectedCurrencies.value.length > 1 || selectedCurrencies.value.length === 0) {
      selectedCurrencies.value = [currencyUsed];
    } else {
      tableRef.value?.reloadPackages();
    }
  }

  // Used to get the packages returned from the deal discovery endpoint, since we need to select them all at once upon applying the filter.
  async function getTargetedPackages(packageIDs: number[]) {
    try {
      const packages = await QueryParams.for({
        top: null,
        filterCollection: new FilterCollection([new Filter('package_id', packageIDs, FilterComparisonOperator.IN)]),
      }).run(getCuratorPackages);
      return packages.items;
    } catch (error) {
      return [];
    }
  }

  function selectChildDeals(packages: Package[]) {
    if (tableRef.value?.table == null) {
      setTimeout(() => selectChildDeals(packages), 100);
      return;
    }
    packages.forEach((p) => {
      tableRef.value.table.handleItemSelection(p, true);
    });
  }

  watch(
    () => deal.value.packages,
    () => {
      selectChildDeals(deal.value.packages || []);
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  [data-key='cell-wrapper-mediaOwner'] {
    img {
      max-width: 10rem;
      max-height: 3rem;
    }
  }

  .badge {
    height: 3rem;
  }
</style>
